import { useState, useMemo, useCallback, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building, BedDouble, RefreshCw, Plus, Edit, Trash2, MapPin, Globe } from "lucide-react";
import { useHotels } from "@/hooks/useHotels";
import { useOptimizedData } from "@/hooks/useOptimizedData";
import { AdvancedSearch, SearchConfig } from "@/components/ui/search-advanced";
import { OptimizedTable, TableColumn } from "@/components/ui/optimized-table";
import { DataExport } from "@/components/ui/data-export";
import { HotelDialog } from "@/components/hotels/HotelDialog";
import { RoomDialog } from "@/components/hotels/RoomDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinnerWithText } from "@/components/ui/loading-spinner";
import { Hotel, Room } from "@/types/hotel";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";

const Hotels = () => {
  const {
    hotels,
    rooms,
    loading,
    saveHotel,
    saveRoom,
    deleteHotel,
    deleteRoom
  } = useHotels();
  const [hotelDialogOpen, setHotelDialogOpen] = useState(false);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingHotel, setEditingHotel] = useState<Hotel | null>(null);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [preselectedHotelId, setPreselectedHotelId] = useState<string>("");
  const [activeTab, setActiveTab] = useState("hotels");

  // Get unique countries and cities for filters
  const countries = [...new Set(hotels.map(hotel => hotel.country))].sort();
  const cities = [...new Set(hotels.map(hotel => hotel.city))].sort();

  // تكوين البحث المتقدم للفنادق
  const hotelsSearchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الفنادق...",
    filters: [{
      id: 'country',
      label: 'الدولة',
      type: 'select',
      options: countries.map(country => ({
        value: country,
        label: country
      })),
      icon: Globe
    }, {
      id: 'city',
      label: 'المدينة',
      type: 'select',
      options: cities.map(city => ({
        value: city,
        label: city
      })),
      icon: MapPin
    }]
  }), [countries, cities]);

  // تكوين البحث المتقدم للغرف
  const roomsSearchConfig: SearchConfig = useMemo(() => ({
    placeholder: "البحث في الغرف...",
    filters: [{
      id: 'country',
      label: 'الدولة',
      type: 'select',
      options: countries.map(country => ({
        value: country,
        label: country
      })),
      icon: Globe
    }, {
      id: 'city',
      label: 'المدينة',
      type: 'select',
      options: cities.map(city => ({
        value: city,
        label: city
      })),
      icon: MapPin
    }]
  }), [countries, cities]);

  // استخدام hook البيانات المحسن للفنادق
  const {
    data: displayHotels,
    allData: filteredHotels,
    searchQuery: hotelsSearchQuery,
    setSearchQuery: setHotelsSearchQuery,
    filters: hotelsFilters,
    addFilter: addHotelsFilter,
    removeFilter: removeHotelsFilter,
    clearFilters: clearHotelsFilters,
    sortConfig: hotelsSortConfig,
    changeSort: changeHotelsSort,
    pagination: hotelsPagination,
    changePage: changeHotelsPage,
    stats: hotelsStats
  } = useOptimizedData({
    data: hotels,
    searchFields: ['name_ar', 'name_en', 'country', 'city'],
    initialPageSize: 15
  });

  // استخدام hook البيانات المحسن للغرف
  const {
    data: displayRooms,
    allData: filteredRooms,
    searchQuery: roomsSearchQuery,
    setSearchQuery: setRoomsSearchQuery,
    filters: roomsFilters,
    addFilter: addRoomsFilter,
    removeFilter: removeRoomsFilter,
    clearFilters: clearRoomsFilters,
    sortConfig: roomsSortConfig,
    changeSort: changeRoomsSort,
    pagination: roomsPagination,
    changePage: changeRoomsPage,
    stats: roomsStats
  } = useOptimizedData({
    data: rooms,
    searchFields: ['name', 'name_en'],
    initialPageSize: 15
  });

  // الإشعارات في الوقت الفعلي
  useEffect(() => {
    const hotelsChannel = supabase.channel('hotels_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'hotels'
    }, payload => {
      console.log('Hotel change detected:', payload);
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة فندق جديد');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات فندق');
          break;
        case 'DELETE':
          toast.warning('تم حذف فندق');
          break;
      }
    }).subscribe();
    const roomsChannel = supabase.channel('rooms_changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'rooms'
    }, payload => {
      console.log('Room change detected:', payload);
      switch (payload.eventType) {
        case 'INSERT':
          toast.success('تم إضافة غرفة جديدة');
          break;
        case 'UPDATE':
          toast.info('تم تحديث بيانات غرفة');
          break;
        case 'DELETE':
          toast.warning('تم حذف غرفة');
          break;
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(hotelsChannel);
      supabase.removeChannel(roomsChannel);
    };
  }, []);

  // تكوين أعمدة جدول الفنادق
  const hotelsTableColumns: TableColumn<Hotel>[] = useMemo(() => [{
    key: 'name_ar',
    title: 'اسم الفندق',
    dataIndex: 'name_ar',
    sortable: true,
    render: (value, record) => <div className="space-y-1">
          <div className="font-medium text-card-foreground">{value}</div>
          {record.name_en && <div className="text-sm text-muted-foreground">{record.name_en}</div>}
        </div>
  }, {
    key: 'country',
    title: 'الدولة',
    dataIndex: 'country',
    sortable: true
  }, {
    key: 'city',
    title: 'المدينة',
    dataIndex: 'city',
    sortable: true
  }, {
    key: 'stars',
    title: 'التقييم',
    dataIndex: 'stars',
    render: value => value ? `${value} ⭐` : '-'
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <Button variant="ghost" size="sm" onClick={() => handleEditHotel(record)} className="h-8 w-8 p-0 hover:bg-primary-light">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleAddRoomToHotel(record.id)} className="h-8 w-8 p-0 hover:bg-success-light">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
  }], []);

  // تكوين أعمدة جدول الغرف
  const roomsTableColumns: TableColumn<Room>[] = useMemo(() => [{
    key: 'name',
    title: 'اسم الغرفة',
    dataIndex: 'name',
    sortable: true,
    render: (value, record) => <div className="space-y-1">
          <div className="font-medium text-card-foreground">{value}</div>
          {record.name_en && <div className="text-sm text-muted-foreground">{record.name_en}</div>}
        </div>
  }, {
    key: 'hotel',
    title: 'الفندق',
    render: (_, record) => record.hotel?.name_ar || '-'
  }, {
    key: 'buying_price',
    title: 'سعر الشراء',
    dataIndex: 'buying_price',
    render: (value, record) => value ? `${value} ${record.currency || 'SAR'}` : '-'
  }, {
    key: 'selling_price',
    title: 'سعر البيع',
    dataIndex: 'selling_price',
    render: (value, record) => value ? `${value} ${record.currency || 'SAR'}` : '-'
  }, {
    key: 'actions',
    title: 'الإجراءات',
    align: 'center',
    render: (_, record) => <div className="flex gap-2 justify-center">
          <Button variant="ghost" size="sm" onClick={() => handleEditRoom(record)} className="h-8 w-8 p-0 hover:bg-primary-light">
            <Edit className="h-4 w-4" />
          </Button>
        </div>
  }], []);

  // معالجات الأحداث
  const handleEditHotel = useCallback((hotel: Hotel) => {
    setEditingHotel(hotel);
    setHotelDialogOpen(true);
  }, []);
  const handleEditRoom = useCallback((room: Room) => {
    setEditingRoom(room);
    setPreselectedHotelId("");
    setRoomDialogOpen(true);
  }, []);
  const handleAddHotel = useCallback(() => {
    setEditingHotel(null);
    setHotelDialogOpen(true);
  }, []);
  const handleAddRoom = useCallback(() => {
    setEditingRoom(null);
    setPreselectedHotelId("");
    setRoomDialogOpen(true);
  }, []);
  const handleAddRoomToHotel = useCallback((hotelId: string) => {
    setEditingRoom(null);
    setPreselectedHotelId(hotelId);
    setRoomDialogOpen(true);
  }, []);
  const handleHotelsSearch = useCallback((query: string, searchFilters: any[]) => {
    setHotelsSearchQuery(query);
    searchFilters.forEach(filter => {
      addHotelsFilter(filter.id, filter.value, filter.type);
    });
  }, [setHotelsSearchQuery, addHotelsFilter]);
  const handleRoomsSearch = useCallback((query: string, searchFilters: any[]) => {
    setRoomsSearchQuery(query);
    searchFilters.forEach(filter => {
      addRoomsFilter(filter.id, filter.value, filter.type);
    });
  }, [setRoomsSearchQuery, addRoomsFilter]);
  const hotelsSearchFilters = useMemo(() => hotelsFilters.map(filter => {
    const config = hotelsSearchConfig.filters?.find(f => f.id === filter.id);
    return {
      ...filter,
      label: config?.label || filter.id,
      options: config?.options
    };
  }), [hotelsFilters, hotelsSearchConfig.filters]);
  const roomsSearchFilters = useMemo(() => roomsFilters.map(filter => {
    const config = roomsSearchConfig.filters?.find(f => f.id === filter.id);
    return {
      ...filter,
      label: config?.label || filter.id,
      options: config?.options
    };
  }), [roomsFilters, roomsSearchConfig.filters]);
  if (loading) {
    return <div className="container mx-auto p-6">
        <LoadingSpinnerWithText text="جاري تحميل الفنادق..." />
      </div>;
  }
  return (
    <ProtectedRoute requiredPermission="الفنادق">
      <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* الهيدر */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-card-foreground">إدارة الفنادق والغرف</h1>
          
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="hotels" className="flex items-center gap-2">
            <Building className="w-4 h-4" />
            الفنادق ({hotelsStats.filtered})
          </TabsTrigger>
          <TabsTrigger value="rooms" className="flex items-center gap-2">
            <BedDouble className="w-4 h-4" />
            الغرف ({roomsStats.filtered})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hotels" className="space-y-4">
          {/* Header للفنادق */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              {/* زر الإضافة */}
              <Button onClick={handleAddHotel} className="gap-2">
                <Plus className="w-4 h-4" />
                إضافة فندق
              </Button>
              
              {/* تصدير البيانات */}
              <DataExport data={filteredHotels} columns={[{
                key: 'name_ar',
                title: 'الاسم العربي',
                dataIndex: 'name_ar'
              }, {
                key: 'name_en',
                title: 'الاسم الإنجليزي',
                dataIndex: 'name_en'
              }, {
                key: 'country',
                title: 'الدولة',
                dataIndex: 'country'
              }, {
                key: 'city',
                title: 'المدينة',
                dataIndex: 'city'
              }]} filename="hotels" formats={['csv', 'json']} />
              
              {/* إحصائيات */}
              <div className="text-sm text-muted-foreground flex gap-4 mr-auto">
                <span>المجموع: {hotelsStats.total}</span>
                <span>المفلتر: {hotelsStats.filtered}</span>
                <span>المعروض: {hotelsStats.showing}</span>
              </div>
            </div>
          </div>

          {/* البحث المتقدم للفنادق */}
          <AdvancedSearch config={hotelsSearchConfig} onSearch={handleHotelsSearch} initialQuery={hotelsSearchQuery} initialFilters={hotelsSearchFilters} />

          {/* جدول الفنادق */}
          <OptimizedTable columns={hotelsTableColumns} data={displayHotels} loading={loading} pagination={{
          current: hotelsPagination.current,
          pageSize: hotelsPagination.pageSize,
          total: hotelsStats.filtered,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 25, 50]
        }} onPaginationChange={changeHotelsPage} sortConfig={hotelsSortConfig ? {
          key: hotelsSortConfig.key as string,
          direction: hotelsSortConfig.direction
        } : undefined} onSortChange={config => config ? changeHotelsSort(config.key as keyof Hotel, config.direction) : changeHotelsSort('id')} rowKey="id" emptyText="لا يوجد فنادق" size="middle" />
        </TabsContent>

        <TabsContent value="rooms" className="space-y-4">
          {/* أدوات الغرف */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>المجموع: {roomsStats.total}</span>
              <span>المفلتر: {roomsStats.filtered}</span>
              <span>المعروض: {roomsStats.showing}</span>
            </div>
            
            <div className="flex gap-2">
              <DataExport data={filteredRooms} columns={[{
              key: 'name',
              title: 'اسم الغرفة',
              dataIndex: 'name'
            }, {
              key: 'hotel_name',
              title: 'الفندق',
              render: (_, record) => record.hotel?.name_ar || '-'
            }, {
              key: 'capacity',
              title: 'السعة',
              dataIndex: 'capacity'
            }, {
              key: 'price_per_night',
              title: 'السعر/ليلة',
              dataIndex: 'price_per_night'
            }]} filename="rooms" formats={['csv', 'json']} />
              
              <Button onClick={handleAddRoom} className="gap-2">
                <Plus className="w-4 h-4" />
                إضافة غرفة
              </Button>
            </div>
          </div>

          {/* البحث المتقدم للغرف */}
          <AdvancedSearch config={roomsSearchConfig} onSearch={handleRoomsSearch} initialQuery={roomsSearchQuery} initialFilters={roomsSearchFilters} />

          {/* جدول الغرف */}
          <OptimizedTable columns={roomsTableColumns} data={displayRooms} loading={loading} pagination={{
          current: roomsPagination.current,
          pageSize: roomsPagination.pageSize,
          total: roomsStats.filtered,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 25, 50]
        }} onPaginationChange={changeRoomsPage} sortConfig={roomsSortConfig ? {
          key: roomsSortConfig.key as string,
          direction: roomsSortConfig.direction
        } : undefined} onSortChange={config => config ? changeRoomsSort(config.key as keyof Room, config.direction) : changeRoomsSort('id')} rowKey="id" emptyText="لا يوجد غرف" size="middle" />
        </TabsContent>
      </Tabs>

      <HotelDialog open={hotelDialogOpen} onOpenChange={setHotelDialogOpen} editingHotel={editingHotel} onSubmit={saveHotel} />

      <RoomDialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen} editingRoom={editingRoom} onSubmit={saveRoom} hotels={hotels} preselectedHotelId={preselectedHotelId} />
    </div>
    </ProtectedRoute>
  );
};
export default Hotels;